<script setup lang="ts">
/**
 * A greenhouse gas inventory, set the way one is read.
 *
 * The lines grouped by scope, a subtotal under each, and two totals at the
 * foot. Every one of those figures is derived: nothing in this table can be
 * typed into a total, which is the whole reason the inventory is a section
 * kind rather than a table with the right headings.
 *
 * Two totals, each labelled with its scope 2 basis. The same electricity is
 * high-carbon on the grid average and near zero on a renewable contract, and a
 * statement that reports one total without saying which basis it used is
 * misleading without having said anything false.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { GHG_SCOPES, ghgTotals, intensityOf, type GhgScope, type ScopeSum } from '@/domain/ghg'
import type { GhgSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: GhgSection }>()

const totals = computed(() => ghgTotals(props.section.lines))

/** The lines of one scope, in the order they were entered. */
function linesOf(scope: GhgScope) {
  return props.section.lines.filter((line) => line.scope === scope)
}

function sumOf(scope: GhgScope): ScopeSum {
  return totals.value[scope]
}

/**
 * A figure as it should be printed.
 *
 * Grouped with a space, which is the French convention and the one a
 * statement of emissions is set in. Nothing rather than a dash where there is
 * no figure: a dash in a column of emissions reads as zero.
 */
function show(value: number | null): string {
  if (value === null) return ''
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(value)
}

/** What a scope 3 line is called: its category, or what somebody typed. */
function nameOf(line: { category: string; label: string }): string {
  const typed = line.label.trim()
  if (typed !== '') return typed
  return line.category === '' ? '' : t(`inspector.ghg.categories.${line.category}`)
}

const intensity = computed(() => ({
  value: intensityOf(totals.value.totalMarket.value, props.section.intensityValue),
  comparative: intensityOf(
    totals.value.totalMarket.comparative,
    props.section.intensityComparative,
  ),
}))
</script>

<template>
  <figure class="ghg">
    <p v-if="section.unit" class="page-unit">{{ section.unit }}</p>

    <table>
      <thead>
        <tr>
          <th>{{ t('inspector.ghg.line') }}</th>
          <th class="is-numeric">{{ section.period || t('inspector.ghg.period') }}</th>
          <th class="is-numeric">{{ section.comparativePeriod }}</th>
        </tr>
      </thead>

      <tbody v-for="scope in GHG_SCOPES" :key="scope">
        <tr class="ghg__scope">
          <th scope="rowgroup" colspan="3">{{ t(`inspector.ghg.scopes.${scope}`) }}</th>
        </tr>

        <tr v-for="line in linesOf(scope)" :key="line.id">
          <td class="ghg__name">{{ nameOf(line) }}</td>
          <td class="is-numeric">{{ line.value }}</td>
          <td class="is-numeric">{{ line.comparative }}</td>
        </tr>

        <tr class="ghg__subtotal">
          <td class="is-strong">{{ t(`inspector.ghg.subtotals.${scope}`) }}</td>
          <td class="is-numeric is-strong">{{ show(sumOf(scope).value) }}</td>
          <td class="is-numeric is-strong">{{ show(sumOf(scope).comparative) }}</td>
        </tr>
      </tbody>

      <tfoot>
        <tr class="ghg__total">
          <td class="is-strong">{{ t('inspector.ghg.totalMarket') }}</td>
          <td class="is-numeric is-strong">{{ show(totals.totalMarket.value) }}</td>
          <td class="is-numeric is-strong">{{ show(totals.totalMarket.comparative) }}</td>
        </tr>
        <tr class="ghg__total">
          <td class="is-strong">{{ t('inspector.ghg.totalLocation') }}</td>
          <td class="is-numeric is-strong">{{ show(totals.totalLocation.value) }}</td>
          <td class="is-numeric is-strong">{{ show(totals.totalLocation.comparative) }}</td>
        </tr>

        <!-- A total that falls while the business doubles is not a reduction,
             which is why the intensity is asked for beside the gross figures. -->
        <tr v-if="section.intensityLabel" class="ghg__intensity">
          <td>{{ section.intensityLabel }}</td>
          <td class="is-numeric">{{ show(intensity.value) }}</td>
          <td class="is-numeric">{{ show(intensity.comparative) }}</td>
        </tr>
      </tfoot>
    </table>

    <figcaption v-if="section.caption" class="page-caption">{{ section.caption }}</figcaption>
  </figure>
</template>

<style scoped>
.ghg {
  margin: 0;
}

/* A scope's own heading, ruled above rather than tinted: a band of colour
   across a statement of emissions is the loudest thing on the page. */
.ghg__scope th {
  padding-top: 3mm;
  border-bottom: 0;
  color: var(--page-accent);
  font-size: 8.5pt;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ghg__name {
  padding-left: 4mm;
}

.ghg__subtotal td {
  border-top: 1px solid var(--paper-rule);
}

.ghg__total td {
  border-top: 1px solid var(--page-accent);
}

.ghg__intensity td {
  border-top: 0;
  color: var(--paper-ink-soft);
  font-size: 9pt;
}
</style>
